"use client";

import de from "dayjs/locale/de";
import { NextIntlClientProvider } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import React, { useEffect, useRef } from "react";
import { SWRConfig } from "swr";
import { ConsentProvider } from "../../components/Consent/provider";
import { DayjsProvider } from "../../components/Dayjs";
import { sendError } from "../../components/ErrorBoundary/helpers";
import { WebviewProvider } from "../../components/WebviewContext";
import { pushDataLayer } from "../../helpers/dataLayer";
import { getWindow } from "../../helpers/getWindow";
import { isNavigatorConnectionSupported } from "../../helpers/isNavigatorConnectionSupported";
import { localeMap } from "../../helpers/localeMap";
import { registerNewrelicErrorHandler } from "../../helpers/newrelicErrorHandler";
import { coreWebVitals } from "../../helpers/webVitals";
import { useCountry } from "../../hooks/useCountry";
import { getTimezone } from "../common/getTimezone";
import { swrConfig } from "../config.generic";
import ProvidersInner from "./providersInner";

type Props = {
    children: React.ReactNode;
};

const Providers: React.FC<Props> = ({ children }) => {
    const country = useCountry();
    const pathname = usePathname();
    const window = getWindow();
    const searchParams = useSearchParams();
    // Use search params as useIsWebview is declared in this component by WebviewProvider.
    const isWebview = searchParams.get("webview") === "app";
    // For webview we don't want to track web vitals.
    const webVitalsInitialized = useRef(searchParams.get("webview") === "app");
    const WebviewWrapper = isWebview ? WebviewProvider : React.Fragment;

    useEffect(() => {
        if (window === undefined) {
            return;
        }

        pushDataLayer({
            type: "event",
            event: "savedata",
            eventCategory: "Save Data Check",
            eventAction: isNavigatorConnectionSupported(window.navigator)
                ? window.navigator.connection.saveData
                : "unsupported",
            eventLabel: window.navigator.userAgent,
            nonInteraction: true,
        });

        if (!webVitalsInitialized.current) {
            coreWebVitals();
            webVitalsInitialized.current = true;
        }
    }, [window]);

    useEffect(() => {
        registerNewrelicErrorHandler();

        window?.newrelic?.setApplicationVersion(
            process.env.NEXT_PUBLIC_BUILD_NUMBER ?? "unknown"
        );
        if (typeof window?.newrelic?.addRelease !== "undefined") {
            window.newrelic.addRelease(
                `web-app-react-${
                    process.env.NEXT_PUBLIC_DEPLOY_ENV ?? "unknown"
                }`,
                process.env.NEXT_PUBLIC_BUILD_NUMBER ?? "unknown"
            );
        }
    }, [window]);

    useEffect(() => {
        // exclude all radars and radar from snow pages
        if (
            window &&
            (!pathname.startsWith("/wetterkarten/") ||
                !pathname.includes("/schneeradar/"))
        ) {
            try {
                window.sessionStorage.setItem("wcom_internal_page", pathname);
            } catch (e) {
                sendError(e);
            }
        }
    }, [window, pathname]);

    return (
        <NextIntlClientProvider
            timeZone={getTimezone(undefined, country)}
            onError={(error) => sendError(error)}
            getMessageFallback={({ namespace, key }) =>
                `${namespace ?? "unknown"}.${key}`
            }
            locale={localeMap[country]}
        >
            <SWRConfig value={swrConfig}>
                <DayjsProvider locale={de}>
                    <WebviewWrapper>
                        <ConsentProvider>
                            <ProvidersInner>{children}</ProvidersInner>
                        </ConsentProvider>
                    </WebviewWrapper>
                </DayjsProvider>
            </SWRConfig>
        </NextIntlClientProvider>
    );
};

export default Providers;
